import type { UpdateAbsenceBody } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { normalizeErrorMessage, showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'
import {
  ABSENCE_DELETE_QUERY_KEY,
  ABSENCES_QUERY_KEY,
  ABSENCES_URL
} from './queryConstants'

type UseAbsenceDeleteProps = {
  absenceId: string | undefined
  onDeleteSuccess?: () => void
}

type UseAbsenceDeleteReturn = {
  deleteAbsence: (values: UpdateAbsenceBody) => Promise<unknown>
  isDeleteLoading: boolean
}

export const useAbsenceDelete = ({
  absenceId,
  onDeleteSuccess
}: UseAbsenceDeleteProps): UseAbsenceDeleteReturn => {
  const queryClient = useQueryClient()
  const handleError = (err: unknown) => {
    showToast({
      title: normalizeErrorMessage(err),
      type: 'error'
    })
  }

  // DELETE
  const deleteFunction = async () => {
    return postJSON({
      url: `${ABSENCES_URL}/${absenceId}`,
      method: 'DELETE',
      isRota: true
    })
  }

  const { mutateAsync: deleteMutation, isLoading: isDeleteLoading } =
    useMutation<unknown, unknown, UpdateAbsenceBody, unknown>(
      [ABSENCE_DELETE_QUERY_KEY, absenceId],
      deleteFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries()
          onDeleteSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const deleteAbsence = async (values: UpdateAbsenceBody) => {
    try {
      const result = await deleteMutation(values)
      queryClient.invalidateQueries(ABSENCES_QUERY_KEY)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    deleteAbsence,
    isDeleteLoading
  }
}
